import { UploadOutlined } from '@ant-design/icons'
import { Button, Col, message, Row, Typography, Upload } from 'antd'
import { useState } from 'react'
import { postUploadUserImage } from '../../services/BookingChecking/bookingCheckin.service'

const { Text } = Typography

interface UploadItemProps {
  name: string
  path: string
  icon: React.ReactNode
  matches: boolean
  userId?: number
  token?: string | null
  onUploadSuccess?: () => Promise<void> | void
}

export const BookingCheckinUploadItem: React.FC<UploadItemProps> = ({
  name,
  path,
  icon,
  matches,
  userId,
  token,
  onUploadSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<string | undefined>(undefined)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isConfirmed, setIsConfirmed] = useState(false)

  const handleFileSelect = (info: any) => {
    if (info.file?.originFileObj) {
      const file = info.file.originFileObj
      setSelectedFile(file)
      const previewUrl = URL.createObjectURL(file)
      setPreview(previewUrl)
      setIsConfirmed(false) // Reset confirmation when new file is selected
    }
  }

  const handleConfirmAndSubmit = async () => {
    if (!selectedFile) {
      message.error('No file selected')
      return
    }

    try {
      setLoading(true)

      if (userId && token) {
        // Create auth context compatible with the new API using the token from URL params
        const authContext = {
          user: {
            id: userId,
            token: token,
          },
          userLoggedIn: {
            userToken: token,
          },
        }

        const formData = new FormData()
        formData.append('fileName', selectedFile)

        // Add required date fields based on document type
        const currentDate = new Date().toISOString().split('T')[0]
        const expirationDate = new Date()

        // Set different expiration dates based on document type
        if (path.includes('license')) {
          expirationDate.setFullYear(expirationDate.getFullYear() + 5) // 5 years for licenses
        } else {
          expirationDate.setFullYear(expirationDate.getFullYear() + 10) // 10 years for ID cards
        }

        formData.append('issueDate', currentDate)
        formData.append('expirationDate', expirationDate.toISOString().split('T')[0])

        const response = await postUploadUserImage(
          authContext,
          { userid: userId, pathfile: path },
          formData,
        )

        message.success(`${selectedFile.name} file uploaded successfully`)
        setIsConfirmed(true)

        // Call the refresh callback to update the displayed images
        if (onUploadSuccess) {
          await onUploadSuccess()
        }
      } else {
        throw new Error('User ID or authentication token not available')
      }
    } catch (error: any) {
      // Enhanced error messages based on status codes
      if (error?.status === 401) {
        message.error('Authentication failed. Please login again.')
      } else if (error?.status === 400) {
        message.error('Invalid file or missing required information.')
      } else {
        message.error(`File upload failed.`)
      }
    } finally {
      setLoading(false)
    }
  }

  const beforeUpload = (file: File) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file types!')
    }
    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      message.error('Image must be smaller than 10MB!')
    }
    return isJpgOrPng && isLt10M
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <Row
        justify="center"
        align="middle"
        style={{
          minWidth: '320px',
          backgroundColor: '#fff',
          padding: matches ? '1.5rem' : '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          margin: '0 auto',
        }}
      >
        <Col>
          {/* Header Section */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: matches ? '1.5rem' : '1rem',
              paddingBottom: '0.5rem',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <Text
              style={{
                fontWeight: 600,
                fontSize: matches ? '16px' : '14px',
              }}
            >
              {name}
            </Text>
          </div>

          {/* Upload Buttons Section */}
          <div
            style={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Upload
              onChange={handleFileSelect}
              beforeUpload={beforeUpload}
              showUploadList={false}
              accept="image/*"
              customRequest={() => false} // Prevent automatic upload
            >
              <Button
                icon={<UploadOutlined />}
                disabled={loading}
                size={matches ? 'middle' : 'small'}
                style={{
                  width: '100%',
                  height: matches ? '40px' : '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div>Choose Image</div>
              </Button>
            </Upload>

            {/* Confirmation Button - Only show when file is selected */}
            {selectedFile && !isConfirmed && (
              <Button
                type="primary"
                loading={loading}
                onClick={handleConfirmAndSubmit}
                size={matches ? 'middle' : 'small'}
                style={{
                  width: '100%',
                  height: matches ? '40px' : '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Confirm & Submit
              </Button>
            )}

            {/* Success indicator when confirmed */}
            {isConfirmed && (
              <Button
                type="primary"
                disabled
                size={matches ? 'middle' : 'small'}
                style={{
                  width: '100%',
                  height: matches ? '40px' : '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderColor: '#52c41a',
                }}
              >
                ✓ Uploaded Successfully
              </Button>
            )}
          </div>

          {/* Preview Section */}
          {preview && (
            <div
              style={{
                marginTop: matches ? '1.5rem' : '1rem',
                textAlign: 'center',
                padding: '1rem',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                border: '1px dashed #d9d9d9',
              }}
            >
              <Text
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '12px',
                  color: '#666',
                }}
              >
                Preview:
              </Text>
              <img
                src={preview}
                alt="Preview"
                style={{
                  width: '100%',
                  maxWidth: matches ? '300px' : '200px',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  )
}
